/**
 * Formula Operator Constants (Frontend)
 *
 * Fetches operator definitions from the API.
 * Single source of truth: API /api/operators endpoint
 */

import type { FieldDataType } from '@loader/shared-types';

import { createRpcClient } from '@/lib/rpc';

export interface OperatorDefinition {
  /** Operator name (what user types) */
  name: string;
  /** Human-readable label */
  label: string;
  /** One-sentence description */
  description?: string;
  /** Number of operands: number or '1+' for variadic */
  arity: number | string;
  /** Category for grouping */
  category: 'arithmetic' | 'comparison' | 'logical' | 'text' | 'math' | 'test' | 'date';
  /** Example usage */
  example?: string;
  /** Return type */
  returnType?: 'number' | 'string' | 'boolean' | 'any';
}

/**
 * Schema column definition for Monaco editor
 */
export interface SchemaColumn {
  /** Column ID (e.g., "sales.amount" or field UUID) */
  id: string;
  /** Display label (optional) */
  label?: string;
  /** Description (optional) */
  description?: string;
  /** Data type (optional, for future type checking) */
  data_type?: FieldDataType;
  /** Table alias (e.g., "orders", "customers") */
  tableAlias?: string;
}

// Cache for operators
let cachedOperators: OperatorDefinition[] = [];
let operatorsFetched = false;

/**
 * Fetch operators from API using RPC client
 * Results are cached after first fetch
 */
export async function fetchOperators(): Promise<OperatorDefinition[]> {
  if (operatorsFetched) {
    return cachedOperators;
  }

  try {
    // Create RPC client instance
    // Note: This is a public endpoint, so token might be optional
    const rpc = createRpcClient(async () => {
      try {
        // Try to get token, but don't fail if unavailable (public endpoint)
        return null;
      } catch {
        return null;
      }
    });

    const res = await rpc.api.private.operators.$get();
    if (!res.ok) throw new Error('Failed to fetch operators');
    const response = await res.json();

    // Check for error response
    if ('error' in response) {
      throw new Error(typeof response.error === 'string' ? response.error : 'Failed to fetch operators');
    }

    // Backend returns { operators: [...], count: N } directly
    const operators = response.operators as OperatorDefinition[];
    cachedOperators = operators || [];
    operatorsFetched = true;
    return cachedOperators;
  } catch {
    // Silently fail — editor works without operators (no autocomplete/validation)
    // Return empty array as fallback
    return [];
  }
}

/**
 * Get all operators (synchronous - requires operators to be fetched first)
 * Use fetchOperators() to load operators before calling this
 */
export function getAllOperators(): OperatorDefinition[] {
  return cachedOperators;
}

